import React from 'react';
import { MessageCircle, ArrowRight } from 'lucide-react';
interface Category {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  href: string;
}
interface WhatCanWeSetupSectionProps {
  title: string;
  categories: {
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    href: string;
  }[];
  ctaText?: string;
  ctaLink?: string;
}
export function WhatCanWeSetupSection({
  title,
  categories,
  ctaText,
  ctaLink
}: WhatCanWeSetupSectionProps) {
  return <section className="py-20 px-6 bg-white/90 backdrop-blur-md" data-id="element-1">
      <div className="max-w-5xl mx-auto" data-id="element-2">
        <h2 className="text-3xl font-bold text-center mb-12" data-id="element-3">{title}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-4">
          {categories.map((category, idx) => <a key={idx} href={category.href} target="_blank" rel="noopener noreferrer" className="group p-6 bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 flex flex-col items-center text-center" data-id="element-5">
              <div className="w-12 h-12 mb-4 text-gray-700 group-hover:text-blue-600 transition-colors" data-id="element-6">
                {category.icon}
              </div>
              <h3 className="font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors" data-id="element-7">
                {category.title}
              </h3>
              <p className="text-sm text-gray-600" data-id="element-8">{category.subtitle}</p>
            </a>)}
        </div>

        {ctaText && ctaLink && <div className="mt-10 text-center" data-id="element-9">
            <p className="text-gray-600 mb-2" data-id="element-10">{ctaText}</p>
            <a href="https://t.me/offgridaccess" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors" data-id="element-11">
              <MessageCircle className="w-4 h-4" data-id="element-12" />
              {ctaLink}
              <ArrowRight className="w-4 h-4" data-id="element-13" />
            </a>
          </div>}
      </div>
    </section>;
}
export const categoryIcons = {
  software: <svg viewBox="0 0 64 64" fill="none" className="w-full h-full" data-id="element-14">
      <defs data-id="element-15">
        <linearGradient id="softwareGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-16">
          <stop offset="0%" style={{
          stopColor: '#667EEA'
        }} data-id="element-17" />
          <stop offset="100%" style={{
          stopColor: '#764BA2'
        }} data-id="element-18" />
        </linearGradient>
      </defs>
      {/* Window frame */}
      <rect x="8" y="12" width="48" height="40" rx="4" fill="url(#softwareGradient)" data-id="element-19" />
      {/* Window header */}
      <rect x="8" y="12" width="48" height="8" rx="4" fill="#5A67D8" data-id="element-20" />
      {/* Window dots */}
      <circle cx="14" cy="16" r="1.5" fill="white" opacity="0.8" data-id="element-21" />
      <circle cx="19" cy="16" r="1.5" fill="white" opacity="0.8" data-id="element-22" />
      <circle cx="24" cy="16" r="1.5" fill="white" opacity="0.8" data-id="element-23" />
      {/* Code brackets */}
      <path d="M20 28l-6 6 6 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-24" />
      <path d="M44 28l6 6-6 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-25" />
      <line x1="28" y1="26" x2="36" y2="42" stroke="white" strokeWidth="2.5" strokeLinecap="round" data-id="element-26" />
    </svg>,
  aiTools: <svg viewBox="0 0 64 64" fill="none" className="w-full h-full" data-id="element-27">
      <defs data-id="element-28">
        <linearGradient id="aiGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-29">
          <stop offset="0%" style={{
          stopColor: '#F093FB'
        }} data-id="element-30" />
          <stop offset="50%" style={{
          stopColor: '#F5576C'
        }} data-id="element-31" />
          <stop offset="100%" style={{
          stopColor: '#4FACFE'
        }} data-id="element-32" />
        </linearGradient>
      </defs>
      {/* Brain/chip outline */}
      <rect x="12" y="12" width="40" height="40" rx="6" fill="url(#aiGradient)" data-id="element-33" />
      {/* Circuit pattern */}
      <circle cx="24" cy="24" r="3" fill="white" data-id="element-34" />
      <circle cx="40" cy="24" r="3" fill="white" data-id="element-35" />
      <circle cx="24" cy="40" r="3" fill="white" data-id="element-36" />
      <circle cx="40" cy="40" r="3" fill="white" data-id="element-37" />
      <circle cx="32" cy="32" r="4" fill="white" data-id="element-38" />
      {/* Connecting lines */}
      <line x1="27" y1="24" x2="37" y2="24" stroke="white" strokeWidth="2" data-id="element-39" />
      <line x1="27" y1="40" x2="37" y2="40" stroke="white" strokeWidth="2" data-id="element-40" />
      <line x1="24" y1="27" x2="24" y2="37" stroke="white" strokeWidth="2" data-id="element-41" />
      <line x1="40" y1="27" x2="40" y2="37" stroke="white" strokeWidth="2" data-id="element-42" />
      <line x1="27" y1="27" x2="29" y2="29" stroke="white" strokeWidth="2" data-id="element-43" />
      <line x1="37" y1="27" x2="35" y2="29" stroke="white" strokeWidth="2" data-id="element-44" />
      <line x1="27" y1="37" x2="29" y2="35" stroke="white" strokeWidth="2" data-id="element-45" />
      <line x1="37" y1="37" x2="35" y2="35" stroke="white" strokeWidth="2" data-id="element-46" />
    </svg>,
  games: <svg viewBox="0 0 64 64" fill="none" className="w-full h-full" data-id="element-47">
      <defs data-id="element-48">
        <linearGradient id="gameGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-49">
          <stop offset="0%" style={{
          stopColor: '#FF6B6B'
        }} data-id="element-50" />
          <stop offset="50%" style={{
          stopColor: '#FF8E53'
        }} data-id="element-51" />
          <stop offset="100%" style={{
          stopColor: '#FFA94D'
        }} data-id="element-52" />
        </linearGradient>
      </defs>
      {/* Controller body */}
      <path d="M16 24C16 20 18 16 24 16h16c6 0 8 4 8 8v8c0 6-4 10-10 10h-4c-2 0-3 1-4 3-1 2-2 3-4 3s-3-1-4-3c-1-2-2-3-4-3h-4c-6 0-10-4-10-10v-8z" fill="url(#gameGradient)" data-id="element-53" />
      {/* D-pad */}
      <rect x="20" y="26" width="3" height="8" rx="1" fill="white" data-id="element-54" />
      <rect x="18" y="28" width="7" height="3" rx="1" fill="white" data-id="element-55" />
      {/* Buttons */}
      <circle cx="42" cy="28" r="2.5" fill="white" data-id="element-56" />
      <circle cx="48" cy="28" r="2.5" fill="white" data-id="element-57" />
      <circle cx="42" cy="34" r="2.5" fill="white" data-id="element-58" />
      <circle cx="48" cy="34" r="2.5" fill="white" data-id="element-59" />
    </svg>,
  accountSetup: <svg viewBox="0 0 64 64" fill="none" className="w-full h-full" data-id="element-60">
      <defs data-id="element-61">
        <linearGradient id="accountGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-62">
          <stop offset="0%" style={{
          stopColor: '#11998E'
        }} data-id="element-63" />
          <stop offset="100%" style={{
          stopColor: '#38EF7D'
        }} data-id="element-64" />
        </linearGradient>
      </defs>
      {/* Shield */}
      <path d="M32 8L16 16v12c0 10 7 19 16 22 9-3 16-12 16-22V16L32 8z" fill="url(#accountGradient)" data-id="element-65" />
      {/* User icon */}
      <circle cx="32" cy="26" r="6" fill="white" data-id="element-66" />
      <path d="M22 42c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-67" />
    </svg>
};